import type { MedplumClient } from "@medplum/core";
import type { Resource, ResourceType } from "@medplum/fhirtypes";
import { Source } from "./Source";

/**
 * Chunk size for the `_id` search that reads the Provenance targets back.
 * The ids ride in the query string, so a long enough list exceeds URL limits.
 */
const SEARCH_BY_DOC_CHUNK_SIZE = 100;

/** Page size for the Provenance target lookup. */
const SOURCE_TARGET_LIMIT = 200;

/**
 * The statics this lookup needs from a model class. Structural, so any
 * `FhirResource` subclass fits without importing the class here.
 *
 * `searchResources` widens to `Resource` because the static on `FhirResource` types its result
 * through a polymorphic `this`, which a structural match cannot resolve. The caller names the
 * concrete resource type through the type argument on the function below.
 */
export interface DocumentSourceModel {
  readonly ResourceType: ResourceType;
  searchResources(
    medplum: MedplumClient,
    query: Record<string, any>,
    options?: { limit?: number },
  ): Promise<Resource[]>;
}

export interface SearchResourcesByDocumentReferenceParams {
  medplum: MedplumClient;
  model: DocumentSourceModel;
  documentReferenceId: string;
  /** Extra filters for the `_id` chunk reads, e.g. a LOINC `code` token that pins the shape. */
  extraQuery?: Record<string, string>;
  /**
   * Search for resources that carry the document link on themselves, from before Provenance
   * recorded it. Omit for a model that never wrote such a link.
   */
  legacyQuery?: Record<string, string | undefined>;
}

/**
 * Returns the resources a `DocumentReference` produced.
 *
 * Reads the Provenance targets, then re-reads them by `_id` in chunks. Merges the result with the
 * legacy search when the model has one. Provenance hits keep their position; a resource found by
 * both paths appears once.
 */
export async function searchResourcesByDocumentReference<TResource extends Resource>({
  medplum,
  model,
  documentReferenceId,
  extraQuery,
  legacyQuery,
}: SearchResourcesByDocumentReferenceParams): Promise<TResource[]> {
  const [references, legacyResources] = await Promise.all([
    Source.searchTargetRefsBySource(medplum, {
      source: { reference: `DocumentReference/${documentReferenceId}` },
      targetResourceType: model.ResourceType,
      limit: SOURCE_TARGET_LIMIT,
    }),
    legacyQuery ? model.searchResources(medplum, legacyQuery) : Promise.resolve([]),
  ]);

  const ids = references
    .map((reference) => Source.extractReferenceId(reference, model.ResourceType))
    .filter((id): id is string => Boolean(id));
  const uniqueIds = Array.from(new Set(ids));

  const sourceResources: Resource[] = [];
  for (let index = 0; index < uniqueIds.length; index += SEARCH_BY_DOC_CHUNK_SIZE) {
    const idChunk = uniqueIds.slice(index, index + SEARCH_BY_DOC_CHUNK_SIZE);
    const chunkResources = await model.searchResources(
      medplum,
      { _id: idChunk.join(","), ...extraQuery },
      { limit: idChunk.length },
    );
    sourceResources.push(...chunkResources);
  }

  const resourceById = new Map<string, Resource>();
  for (const resource of [...sourceResources, ...legacyResources]) {
    if (!resource.id) {
      continue;
    }
    resourceById.set(resource.id, resource);
  }

  return Array.from(resourceById.values()) as TResource[];
}
